import { assertUnreachable } from '@dreryk/react-common';

import {
   AdditionalEntitlement,
   getDraftDisplayIndex,
   isMagistralDrugPrescription,
   isStandardPrescription,
   mapSentPrescriptionPackagesToDraftPrescription,
   SentPrescriptionPackage,
   UnsentPrescription,
} from '~libs.shared.prescription';
import { compact } from '~libs.typescript';

import { PatientId } from '../../PrescriptionIssuingPanel.types';
import { DraftPrescriptionDTO } from '../prescriptions.types';

import { DraftPrescription } from './draftPrescriptions.types';
import { DraftPrescriptionField, DraftPrescriptionsFormBean } from './DraftPrescriptionsForm.types';
import {
   mapDraftMagistralDrugPrescriptionToDraftPrescriptionDTO,
   mapPrescriptionToDraftMagistralDrugPrescription,
} from './magistralDrugPrescription/DraftMagistralDrugPrescriptionField.utils';
import {
   mapDraftStandardPrescriptionToDraftPrescriptionDTO,
   mapPrescriptionToDraftStandardPrescription,
} from './standardPrescription/DraftStandardPrescriptionField.utils';

const mapDraftPrescriptionToField = (
   unsentPrescriptions: UnsentPrescription,
   patientId: PatientId
): DraftPrescriptionField => {
   if (isStandardPrescription(unsentPrescriptions)) {
      return mapPrescriptionToDraftStandardPrescription(unsentPrescriptions, patientId);
   }

   if (isMagistralDrugPrescription(unsentPrescriptions)) {
      return mapPrescriptionToDraftMagistralDrugPrescription(unsentPrescriptions, patientId);
   }

   throw new Error('Not supported type of prescription.');
};

export const mapPrescriptionPackagesToDraftPrescriptionFields = (
   unsentPrescriptions: UnsentPrescription[],
   patientId: PatientId
): DraftPrescriptionField[] => {
   return unsentPrescriptions.map((prescription) => mapDraftPrescriptionToField(prescription, patientId));
};

const mapDraftPrescriptionFieldToDraftPrescriptionDTO = (
   draftPrescriptionField: DraftPrescriptionField
): DraftPrescriptionDTO => {
   const { type } = draftPrescriptionField;

   switch (type) {
      case 'standard':
         return mapDraftStandardPrescriptionToDraftPrescriptionDTO(draftPrescriptionField);
      case 'magistralDrug':
         return mapDraftMagistralDrugPrescriptionToDraftPrescriptionDTO(draftPrescriptionField);
      default:
         return assertUnreachable(type);
   }
};

type PrescriptionsListCreatorOptions = {
   sentPrescriptionPackages: SentPrescriptionPackage[];
   draftPrescriptions: DraftPrescriptionsFormBean['draftPrescriptions'];
};

export const createListDraftPrescriptionDTO = (
   options: PrescriptionsListCreatorOptions
): (DraftPrescriptionDTO & { displayIndex: number })[] => {
   const sentPrescriptions = options.sentPrescriptionPackages.flatMap(
      (prescriptionPackage) => prescriptionPackage.prescriptions
   );

   return [
      ...sentPrescriptions.map(mapSentPrescriptionPackagesToDraftPrescription),
      ...options.draftPrescriptions.map((draftPrescription, index) => ({
         ...mapDraftPrescriptionFieldToDraftPrescriptionDTO(draftPrescription),
         displayIndex: getDraftDisplayIndex(sentPrescriptions, options.draftPrescriptions.length, index),
      })),
   ];
};

export const getAdditionalEntitlements = (draftPrescriptions: DraftPrescription[]): AdditionalEntitlement[] => {
   return compact(draftPrescriptions.flatMap((prescription) => prescription.data.additionalEntitlements));
};
